import type { FormEvent, ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { t } from '@/locales/i18n';

const SIZES = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
} as const;

export type DialogSize = keyof typeof SIZES;

interface FormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  /** Form submit handler — receives the form event, **MUST call event.preventDefault()** itself or pass a react-hook-form `handleSubmit(...)` result. */
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  isSubmitting?: boolean;
  submitLabel?: string;
  cancelLabel?: string;
  size?: DialogSize;
  /** Disable submit button */
  submitDisabled?: boolean;
  children: ReactNode;
  /** Extra footer content rendered before the Cancel/Save buttons (left-aligned). */
  footerExtra?: ReactNode;
}

export function FormDialog({
  open,
  onOpenChange,
  title,
  description,
  onSubmit,
  isSubmitting = false,
  submitLabel,
  cancelLabel,
  size = 'lg',
  submitDisabled = false,
  children,
  footerExtra,
}: FormDialogProps): JSX.Element {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={SIZES[size]}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <form onSubmit={onSubmit} noValidate className="flex min-h-0 flex-1 flex-col">
          <DialogBody className="space-y-6">{children}</DialogBody>
          <DialogFooter className={cn(footerExtra && 'sm:justify-between')}>
            {footerExtra ? <div className="flex items-center gap-2">{footerExtra}</div> : null}
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                {cancelLabel ?? t('common.cancel')}
              </Button>
              <Button type="submit" disabled={isSubmitting || submitDisabled}>
                {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {isSubmitting ? t('common.saving') : (submitLabel ?? t('common.save'))}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
